import { Typography, TypographyProps } from '@mui/material';
import moment from 'moment';
import invariant from 'tiny-invariant';

import { IOptionalTimeWindow } from '../../types/datetimes';
import { NullableFormattedDate, formatDate } from '../../utils/datetimes/formatting';
import { VisitSingleDateDisplay } from '../dates/CommonDateDisplays';
import { CaptionLabel } from '../labels/CaptionLabel';
import { NoneLabel } from '../labels/NoneLabel';
import { VerticalStack } from '../utils/linearLayouts';
import { OptionalDateFulfillmentWindowDisplay } from './OptionalDateFulfillmentWindowDisplay';

export function OptionalDatetimeFulfillmentWindowDisplay({
    fulfillmentWindow: { startDatetime, endDatetime, asDateWindow },
    timezone,
    label = true,
    dateProps,
}: {
    fulfillmentWindow: IOptionalTimeWindow;
    timezone: string;
    label?: boolean;
    dateProps?: TypographyProps;
}) {
    // If the time window maps cleanly to a date window, display the date window instead.
    if (asDateWindow) {
        return (
            <OptionalDateFulfillmentWindowDisplay fulfillmentWindow={asDateWindow} label={label} />
        );
    }

    const startMoment = startDatetime ? moment(startDatetime).tz(timezone) : null;
    const endMoment = endDatetime ? moment(endDatetime).tz(timezone) : null;
    if (startMoment && endMoment) {
        invariant(startDatetime && endDatetime);
        if (startMoment.isSame(endMoment, 'day')) {
            return (
                <VisitSingleDateDisplay
                    label={label}
                    startDate={startDatetime}
                    timezone={timezone}
                />
            );
        }
        return (
            <VerticalStack>
                {label ? <CaptionLabel>Fulfillment Window</CaptionLabel> : null}
                <Typography {...dateProps}>
                    {formatDate({
                        datetime: startMoment,
                        timezone: timezone,
                        alwaysShowYear: false,
                    })}{' '}
                    -{' '}
                    {formatDate({ datetime: endMoment, timezone: timezone, alwaysShowYear: false })}
                </Typography>
            </VerticalStack>
        );
    }
    if (startMoment) {
        return (
            <VerticalStack>
                {label ? <CaptionLabel>Fulfillment Window</CaptionLabel> : null}
                <Typography {...dateProps}>
                    After <NullableFormattedDate date={startMoment} />
                </Typography>
            </VerticalStack>
        );
    }
    if (endMoment) {
        return (
            <VerticalStack>
                {label ? <CaptionLabel>Fulfillment Window</CaptionLabel> : null}
                <Typography {...dateProps}>
                    Before <NullableFormattedDate date={endMoment} />
                </Typography>
            </VerticalStack>
        );
    }

    return <NoneLabel />;
}
